import { jsPDF } from 'jspdf';

// Cache for loaded fonts
// This allows us to reuse the font data without re-downloading
const fontCache: { [key: string]: any } = {};

// Map of font names to their file names
const FONT_FILES: { [key: string]: { normal: string; bold: string, italic: string } } = {
  'Roboto': {
    normal: 'Roboto-normal',
    bold: 'Roboto-bold',
    italic: 'Roboto-italic'
  },
  'NotoSansSC': {
    normal: 'NotoSansSC-normal',
    bold: 'NotoSansSC-bold',
    italic: ''
  },
  'NotoSansKR': {
    normal: 'NotoSansKR-normal',
    bold: 'NotoSansKR-bold',
    italic: ''
  },
  'NotoSansArabic': {
    normal: 'NotoSansArabic-normal',
    bold: 'NotoSansArabic-bold',
    italic: ''
  }
};

// Lazily loads a font file and registers it with jsPDF
// Uses caching to avoid re-downloading font files, but always registers with the PDF instance

export async function loadFont(
  pdf: jsPDF,
  fontName: string,
  weight: 'normal' | 'bold' | 'italic'
): Promise<void> {
  const cacheKey = `${fontName}-${weight}`;

  // Get font file name
  const fileName = FONT_FILES[fontName]?.[weight];
  if (!fileName) {
    console.warn(`Font ${fontName} (${weight}) not found in font files map`);
    return;
  }

  try {
    // Check if the font is already registered in PDF instance to avoid duplicate registration
    const fontList = (pdf as any).getFontList();
    if (fontList[fontName] && fontList[fontName].includes(weight)) {
      return;
    }

    // Import the font module only once per session, but always register for each PDF
    let fontModule;
    if (!fontCache[cacheKey]) {
      fontModule = await import(`../fonts/${fileName}.js`);
      fontCache[cacheKey] = fontModule;
    } else {
      fontModule = fontCache[cacheKey];
    }

    // Register the font with the PDF instance
    if (fontModule.fontConfig) {
      pdf.addFileToVFS(
        fontModule.fontConfig.fileName,
        fontModule.fontConfig.fontData
      );
      pdf.addFont(
        fontModule.fontConfig.fileName,
        fontModule.fontConfig.fontName,
        fontModule.fontConfig.weight
      );
    }
  } catch (error) {
    console.error(`Failed to load font ${fontName} (${weight}):`, error);
  }
}

// Helper functions to detect different language scripts

export function isChinese(text: string): boolean {
  return /[\u4E00-\u9FFF]/.test(text);
}

export function isKorean(text: string): boolean {
  return /[\uAC00-\uD7AF]/.test(text);
}

export function isArabic(text: string): boolean {
  return /[\u0600-\u06FF]/.test(text);
}

// Detect the language context for a piece of text

export function detectLanguageContext(text: string): {
  isChinese: boolean;
  isKorean: boolean;
  isArabic: boolean;
} {
  return {
    isChinese: isChinese(text),
    isKorean: isKorean(text),
    isArabic: isArabic(text)
  };
}


// Detect which fonts are needed for an array of messages
export function detectNeededFonts(messages: Array<{ content: string }>): Set<string> {
  const neededFonts = new Set<string>();
  neededFonts.add('Roboto'); // Always need Roboto for Latin text

  // Combine all message content to get full context
  const fullText = messages.map(m => m.content).join(' ');
  const context = detectLanguageContext(fullText);

  // Add fonts based on detected context
  if (context.isChinese) {
    neededFonts.add('NotoSansSC');
  }
  if (context.isKorean) {
    neededFonts.add('NotoSansKR');
  }
  if (context.isArabic) {
    neededFonts.add('NotoSansArabic');
  }

  return neededFonts;
}

// Pre-load all fonts needed for a set of messages
// This should be called before creating the PDF

export async function preloadFontsForMessages(
  pdf: jsPDF,
  messages: Array<{ content: string }>
): Promise<void> {
  const neededFonts = detectNeededFonts(messages);

  //console.log(`Detected fonts needed:`, Array.from(neededFonts));

  // Load all needed fonts in parallel
  const fontPromises: Promise<void>[] = [];

  for (const fontName of neededFonts) {
    // Load both normal and bold variants
    fontPromises.push(loadFont(pdf, fontName, 'normal'));
    fontPromises.push(loadFont(pdf, fontName, 'bold'));

    // Only load italic if the font has an italic variant defined
    if (FONT_FILES[fontName]?.italic) {
      fontPromises.push(loadFont(pdf, fontName, 'italic'));
    }
  }

  // Wait for all fonts to load
  await Promise.all(fontPromises);
}
